// ============================================================
//  Typography Utils — Google Fonts & Typography Management
// ============================================================

export interface TypographyProps {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
}

export interface FontOption {
  name: string;
  value: string;
  category: 'google' | 'system';
}

// Popular Google Fonts for events/business
export const GOOGLE_FONTS: FontOption[] = [
  { name: 'Marianne', value: 'Marianne', category: 'google' },
  { name: 'Roboto', value: 'Roboto', category: 'google' },
  { name: 'Open Sans', value: 'Open Sans', category: 'google' },
  { name: 'Lato', value: 'Lato', category: 'google' },
  { name: 'Montserrat', value: 'Montserrat', category: 'google' },
  { name: 'Poppins', value: 'Poppins', category: 'google' },
  { name: 'Inter', value: 'Inter', category: 'google' },
  { name: 'Nunito', value: 'Nunito', category: 'google' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'google' },
  { name: 'Playfair Display', value: 'Playfair Display', category: 'google' },
  { name: 'Oswald', value: 'Oswald', category: 'google' },
];

// System fonts as fallbacks
export const SYSTEM_FONTS: FontOption[] = [
  { name: 'Arial', value: 'Arial, sans-serif', category: 'system' },
  {
    name: 'Helvetica',
    value: 'Helvetica, Arial, sans-serif',
    category: 'system',
  },
  {
    name: 'Times New Roman',
    value: 'Times New Roman, Times, serif',
    category: 'system',
  },
  { name: 'Georgia', value: 'Georgia, serif', category: 'system' },
  { name: 'Courier New', value: 'Courier New, monospace', category: 'system' },
];

export const ALL_FONTS: FontOption[] = [...GOOGLE_FONTS, ...SYSTEM_FONTS];

export const FONT_WEIGHTS = [
  { label: '100 - Thin', value: '100' },
  { label: '200 - Extra Light', value: '200' },
  { label: '300 - Light', value: '300' },
  { label: '400 - Regular', value: '400' },
  { label: '500 - Medium', value: '500' },
  { label: '600 - Semi Bold', value: '600' },
  { label: '700 - Bold', value: '700' },
  { label: '800 - Extra Bold', value: '800' },
  { label: '900 - Black', value: '900' },
];

// Font size presets
export const FONT_SIZE_PRESETS = [
  { label: 'XS (12px)', value: '12px' },
  { label: 'SM (14px)', value: '14px' },
  { label: 'Base (16px)', value: '16px' },
  { label: 'LG (18px)', value: '18px' },
  { label: 'XL (20px)', value: '20px' },
  { label: '2XL (24px)', value: '24px' },
  { label: '3XL (30px)', value: '30px' },
  { label: '4XL (36px)', value: '36px' },
  { label: '5XL (48px)', value: '48px' },
  { label: '6XL (60px)', value: '60px' },
];

/**
 * Load Google Font dynamically
 */
export function loadGoogleFont(fontFamily: string): void {
  if (!fontFamily || SYSTEM_FONTS.some((f) => f.value.includes(fontFamily))) {
    return; // Skip system fonts
  }

  const linkId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;

  // Don't load if already loaded
  if (document.getElementById(linkId)) {
    return;
  }

  // Special handling for Marianne (official French government typeface)
  if (fontFamily === 'Marianne') {
    // Add CSS for font-face directly
    const style = document.createElement('style');
    style.id = linkId;
    style.innerHTML = `
      @font-face {
        font-family: 'Marianne';
        src: url('https://cdn.jsdelivr.net/npm/@gouvfr/dsfr@1.11/dist/fonts/Marianne-Regular.woff2') format('woff2'),
             url('https://cdn.jsdelivr.net/npm/@gouvfr/dsfr@1.11/dist/fonts/Marianne-Regular.woff') format('woff');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Marianne';
        src: url('https://cdn.jsdelivr.net/npm/@gouvfr/dsfr@1.11/dist/fonts/Marianne-Bold.woff2') format('woff2'),
             url('https://cdn.jsdelivr.net/npm/@gouvfr/dsfr@1.11/dist/fonts/Marianne-Bold.woff') format('woff');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  } else {
    // Standard Google Fonts loading
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      fontFamily
    )}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
    document.head.appendChild(link);
  }
}

/**
 * Get computed typography styles for rendering
 */
export function getTypographyStyles(
  props: TypographyProps
): React.CSSProperties {
  const { fontFamily, fontSize, fontWeight, textAlign } = props;

  // Load Google Font if needed
  if (fontFamily && GOOGLE_FONTS.some((f) => f.value === fontFamily)) {
    loadGoogleFont(fontFamily);
  }

  return {
    fontFamily: fontFamily ?? undefined,
    fontSize: fontSize ?? undefined,
    fontWeight: fontWeight ?? undefined,
    textAlign: textAlign ?? undefined,
  };
}

/**
 * Get font display name for UI
 */
export function getFontDisplayName(fontValue: string): string {
  const font = ALL_FONTS.find((f) => f.value === fontValue);
  return font ? font.name : fontValue;
}

/**
 * Preload commonly used Google Fonts
 */
export function preloadCommonFonts(): void {
  const commonFonts = ['Marianne', 'Roboto', 'Open Sans', 'Lato', 'Montserrat'];
  commonFonts.forEach(loadGoogleFont);
}
